import { JobTetrisMark } from '../../components/ui/JobTetrisMark';

/**
 * Screen 11 — Session bootstrap loading screen.
 * Triggers during OIDC token exchange (jt_token → Engage session).
 */
export function SessionLoadingPage() {
  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100vw',
        backgroundColor: 'var(--color-surface-page)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-8)',
        position: 'relative',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <style>{`
        @keyframes jt-spin { to { transform: rotate(360deg); } }
        @keyframes jt-dot-pulse {
          0%, 80%, 100% { opacity: 0.25; transform: scale(0.85); }
          40% { opacity: 1; transform: scale(1); }
        }
      `}</style>

      {/* Handoff logos */}
      <HandoffLogos />

      {/* Spinner */}
      <div
        role="status"
        aria-label="Loading"
        style={{
          marginTop: 'var(--space-10)',
          width: '32px',
          height: '32px',
          border: '3px solid var(--color-border-default)',
          borderTopColor: 'var(--color-brand-blue)',
          borderRadius: 'var(--radius-full)',
          animation: 'jt-spin 0.9s linear infinite',
        }}
      />

      {/* Headline + sub */}
      <h1
        style={{
          marginTop: 'var(--space-6)',
          fontSize: 'var(--text-3xl)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--color-text-brand)',
          letterSpacing: 'var(--tracking-tight)',
          lineHeight: 'var(--leading-tight)',
        }}
      >
        Connecting to JobTetris…
      </h1>
      <p
        style={{
          marginTop: 'var(--space-2)',
          fontSize: 'var(--text-md)',
          fontWeight: 'var(--font-weight-regular)',
          color: 'var(--color-text-muted)',
          lineHeight: 'var(--leading-normal)',
          textAlign: 'center',
          maxWidth: '420px',
        }}
      >
        Verifying your session, syncing lists and candidates.
      </p>

      {/* Three-dot progress indicator */}
      <div
        aria-hidden="true"
        style={{
          marginTop: 'var(--space-6)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
        }}
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'var(--color-brand-indigo)',
              display: 'inline-block',
              animation: `jt-dot-pulse 1.2s ${i * 0.18}s ease-in-out infinite both`,
            }}
          />
        ))}
      </div>

      {/* Footer */}
      <p
        style={{
          position: 'absolute',
          bottom: 'var(--space-6)',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: 'var(--text-xs)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--color-text-subtle)',
          letterSpacing: 'var(--tracking-wide)',
          whiteSpace: 'nowrap',
        }}
      >
        Secure handoff via api.jobtetris.com · Signed session token · Engage v1.1
      </p>
    </div>
  );
}

/* ── Handoff logos: JobTetris ⟶ divider ⟶ Engage ── */

function HandoffLogos() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-5)',
      }}
    >
      {/* JobTetris mark */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <JobTetrisMark size={32} decorative />
        <span
          style={{
            fontSize: 'var(--text-xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-text-brand)',
            letterSpacing: 'var(--tracking-tight)',
          }}
        >
          JobTetris
        </span>
      </div>

      {/* Divider */}
      <span
        aria-hidden="true"
        style={{
          width: '1px',
          height: '28px',
          backgroundColor: 'var(--color-border-default)',
        }}
      />

      {/* Engage mark */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            borderRadius: 'var(--radius-input)',
            backgroundColor: 'var(--color-brand-blue)',
            flexShrink: 0,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 12 12" fill="none">
            <path
              d="M6 1L11 6L6 11M1 6H11"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span
          style={{
            fontSize: 'var(--text-xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-text-brand)',
            letterSpacing: 'var(--tracking-tight)',
          }}
        >
          Engage
        </span>
      </div>
    </div>
  );
}
